import { supabase } from '../../lib/supabase';
import type { Story, Band, Profile } from '../../types/database';

export type StoryWithDetails = Story & {
  author: Profile;
  band: Band;
  likes_count: number;
};

export async function fetchStoryDetails(
  bandSlug: string,
  storySlug: string
): Promise<StoryWithDetails> {
  try {
    // First get the band ID from the slug
    const { data: band, error: bandError } = await supabase
      .from('bands')
      .select('id')
      .eq('slug', bandSlug)
      .single();

    if (bandError) throw bandError;
    if (!band) throw new Error('Band not found');

    // Fetch story details
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .select(`
        *,
        author:profiles!stories_author_id_fkey(*),
        band:bands!stories_band_id_fkey(*)
      `)
      .eq('band_id', band.id)
      .eq('slug', storySlug)
      .single();

    if (storyError) throw storyError;
    if (!story) throw new Error('Story not found');

    // Get likes count
    const { count: likesCount } = await supabase
      .from('story_likes')
      .select('*', { count: 'exact' })
      .eq('story_id', story.id);

    return {
      ...story,
      likes_count: likesCount || 0,
    } as StoryWithDetails;
  } catch (error) {
    console.error('Error fetching story details:', error);
    throw error;
  }
}

export async function updateStory(
  id: string,
  data: Partial<Story>
): Promise<Story> {
  try {
    const { data: updatedStory, error } = await supabase
      .from('stories')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!updatedStory) throw new Error('Failed to update story');

    return updatedStory;
  } catch (error) {
    console.error('Error updating story:', error);
    throw error;
  }
}

export async function deleteStory(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('stories')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
}